import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services/auth'
import { AlertCircle } from 'lucide-react'
import PasswordInput from '../../../components/PasswordInput'
import { useTheme } from '../../../contexts/TenantThemeContext'
import BrandBadge from '../../../components/BrandBadge'
import { registerPush } from '../../../lib/pushNotifications'

export default function LoginPage() {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)
  const navigate = useNavigate()
  const { appName, tagline, logoUrl, refreshTheme } = useTheme()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await authService.login(email, password)
      // App.tsx's usePushNotifications() only registers once, at the app's
      // initial mount — it never re-fires for an in-session user switch
      // (logout does a hard reload, but login navigates client-side, so a
      // second user logging in on the same device never re-registers a
      // token under their own user_id). Register explicitly here so every
      // successful login gets its own token, regardless of mount history.
      registerPush(navigate)
      // Apply tenant branding now that we have auth — don't await, navigate immediately
      refreshTheme()
      navigate(data?.user?.role === 'BACKEND_ADMIN' ? '/admin' : '/dashboard')
    } catch {
      setError('Invalid email or password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--surface-body)' }}
    >
      <div className="w-full max-w-sm">
        {/* Brand header */}
        <div
          className="rounded-t-xl flex flex-col items-center py-8 px-8"
          style={{
            background: 'var(--login-brand-bg)',
            borderBottom: '1px solid var(--sidebar-border)',
          }}
        >
          <BrandBadge size={88} logoUrl={logoUrl} appName={appName} />
          <h1 className="text-xl font-bold mt-4 tracking-tight" style={{ color: 'var(--sidebar-text-active)' }}>
            {appName}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--sidebar-section-text)' }}>
            {tagline}
          </p>
        </div>

        {/* Form card */}
        <div
          className="rounded-b-xl px-8 py-7"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderTop: 'none',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <h2
            className="text-lg font-semibold mb-1"
            style={{ color: 'var(--text-primary)' }}
          >
            Sign in to your account
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-tertiary)' }}>
            Enter your credentials to continue
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="alert-danger" data-testid="login-error">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@dealership.com"
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              className="btn-primary w-full justify-center"
              style={{ height: '38px', fontSize: '14px' }}
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-xs text-center mt-5" style={{ color: 'var(--text-tertiary)' }}>
            Contact your administrator to reset your password
          </p>
        </div>
      </div>
    </div>
  )
}
